
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { collectionsApi } from "@/lib/api";
import toast from "react-hot-toast";

interface Collection {
  id: number;
  title: string;
  slug: string;
  description: string | null;
  image_url: string | null;
  gradient: string | null;
  products_count: number;
  is_active: boolean;
  display_order: number;
}

export default function Collections() {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        setLoading(true);
        const response = await collectionsApi.getAll();
        setCollections(response.collections || []);
      } catch (error) {
        console.error("Error fetching collections:", error);
        toast.error("Failed to load collections");
        // Fallback to empty array
        setCollections([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCollections();
  }, []);

  if (loading) {
    return (
      <section id="shop-by-collection" className="py-12 bg-gradient-to-b from-white to-gray-50 mt-4 mb-4 scroll-mt-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl md:text-4xl font-bold font-playfair mb-3">
              Shop by <span className="gradient-text">Collection</span>
            </h2>
            <p className="text-gray-600 text-base max-w-2xl mx-auto">
              Discover our curated collections designed for every occasion
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[1, 2, 3, 4].map((i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="relative h-64 md:h-80 rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-sm"
              >
                {/* Skeleton gradient background */}
                <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 opacity-20 animate-pulse" />
                
                {/* Skeleton content */}
                <div className="relative z-10 h-full flex flex-col justify-between p-8">
                  <div className="space-y-4">
                    {/* Products count skeleton */}
                    <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                    
                    {/* Title skeleton */}
                    <div className="h-8 w-32 bg-gray-300 rounded animate-pulse" />
                    
                    {/* Description skeleton */}
                    <div className="space-y-2">
                      <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
                      <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse" />
                    </div>
                  </div>
                  
                  {/* Explore button skeleton */}
                  <div className="h-6 w-24 bg-gray-300 rounded animate-pulse" />
                </div>
                
                {/* Decorative elements skeleton */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gray-200 opacity-10 rounded-full -mr-16 -mt-16 animate-pulse" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-gray-200 opacity-10 rounded-full -ml-12 -mb-12 animate-pulse" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (collections.length === 0) {
    return null;
  }
  return (
    <section id="shop-by-collection" className="py-12 bg-gradient-to-b from-white to-gray-50 mt-4 mb-4 scroll-mt-24">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h2 className="text-3xl md:text-4xl font-bold font-playfair mb-3">
            Shop by <span className="gradient-text">Collection</span>
          </h2>
          <p className="text-gray-600 text-base max-w-2xl mx-auto">
            Discover our curated collections designed for every occasion
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {collections.map((collection, index) => {
            // Format products count
            const countDisplay = collection.products_count > 0 
              ? `${collection.products_count}+` 
              : "0";
            
            // Default gradient if not provided
            const gradient = collection.gradient || "from-purple-500 to-pink-500";
            
            return (
              <motion.div
                key={collection.id}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="relative h-64 md:h-80 rounded-2xl overflow-hidden group bg-white border border-gray-200 shadow-sm"
              >
                {/* Subtle Gradient Background */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-10 group-hover:opacity-15 transition-opacity duration-300`}
                />

                {/* Content */}
                <div className="relative z-10 h-full flex flex-col justify-between p-8 text-gray-900">
                  <div>
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 }}
                      className="text-sm font-semibold mb-2 text-gray-600"
                    >
                      {countDisplay} Products
                    </motion.div>
                    <motion.h3
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 }}
                      className="text-3xl md:text-4xl font-bold font-playfair mb-2 text-gray-900"
                    >
                      {collection.title}
                    </motion.h3>
                    <motion.p
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.4 }}
                      className="text-lg text-gray-700"
                    >
                      {collection.description || "Discover our curated collection"}
                    </motion.p>
                  </div>

                  <Link to={`/products?collection=${collection.slug}`}>
                    <motion.button
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      whileHover={{ x: 5 }}
                      className="flex items-center space-x-2 text-lg font-semibold text-gray-900 hover:text-purple-600 transition-colors"
                    >
                      <span>Explore</span>
                      <ArrowRight className="w-5 h-5" />
                    </motion.button>
                  </Link>
                </div>

                {/* Decorative Elements */}
                <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${gradient} opacity-5 rounded-full -mr-16 -mt-16`} />
                <div className={`absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-br ${gradient} opacity-5 rounded-full -ml-12 -mb-12`} />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
